import Axios, { AxiosError } from "axios";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import type { CaseShapeFormData } from "src/forms/UploadCaseShapeForm";
import { caseKeys } from "src/query-keys";
import { Case, APIError, type CaseShapeUploadResponse, type FeatureSelectionLayer } from "src/types";

export type CasePredefinedMutationData = {
  name: string;
  description: string;
  userSrid: number;
  polygon_frontendSrid: string;
};

export type CaseGeneralSettingsMutationData = {
  name: string;
  description: string;
  ospmEnabled: boolean;
};

export function useCaseDeleteMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(id: string | number): Promise<void> {
    return Axios.delete(`/planning-api/cases/${id}`);
  }
  return useMutation(() => mutationFn(caseId), {
    // When mutate is called:
    onMutate: async () => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(caseKeys.all);

      // Snapshot the previous value
      const previousCases = queryClient.getQueryData<Case[]>(caseKeys.all);

      if (!previousCases) return;

      // Optimistically update to the new value
      queryClient.setQueryData(
        caseKeys.all,
        previousCases.filter((oldCase) => {
          return oldCase.id !== caseId;
        })
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries(caseKeys.all);
    },
  });
}

export function useCaseCreatePredefinedMutation() {
  const queryClient = useQueryClient();

  async function mutationFn(data: CasePredefinedMutationData): Promise<void> {
    return Axios.post("/planning-api/cases/predefined", data);
  }
  return useMutation<void, AxiosError<APIError[]>, CasePredefinedMutationData, unknown>(
    (values: CasePredefinedMutationData) => mutationFn(values),
    {
      onSettled: () => queryClient.invalidateQueries(caseKeys.all),
    }
  );
}

export function useCaseGeneralSettingsUpdateMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(caseId: number | string, data: CaseGeneralSettingsMutationData): Promise<void> {
    return Axios.put(`/planning-api/cases/${caseId}/generalSettings`, data);
  }
  return useMutation<void, AxiosError<APIError[]>, CaseGeneralSettingsMutationData, unknown>(
    (values: CaseGeneralSettingsMutationData) => mutationFn(caseId, values),
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(caseKeys.all);
        await queryClient.invalidateQueries(caseKeys.detail(caseId));
      },
    }
  );
}

export function useCaseOsmpUpdateMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(caseId: number | string, ospmEnabled: boolean): Promise<void> {
    await Axios.put(`/planning-api/cases/${caseId}/ospmEnabled`, { ospmEnabled: ospmEnabled });
  }
  return useMutation<void, AxiosError<APIError[]>, boolean, unknown>(
    (ospmEnabled: boolean) => mutationFn(caseId, ospmEnabled),
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(caseKeys.all);
        await queryClient.invalidateQueries(caseKeys.detail(caseId));
      },
    }
  );
}

export function useCaseShapeUploadMutation() {
  async function mutationFn(data: CaseShapeFormData): Promise<CaseShapeUploadResponse> {
    const response = await Axios.put<CaseShapeUploadResponse>("/planning-api/cases/import", data.caseShapefile);
    return response.data;
  }
  return useMutation<CaseShapeUploadResponse, AxiosError<APIError[]>, CaseShapeFormData, unknown>(
    (values: CaseShapeFormData) => mutationFn(values)
  );
}

export function useCaseShapeSelectionLayers() {
  async function queryFn() {
    const response = await Axios.get("/planning-api/genericlocations");
    return response.data;
  }
  return useQuery<FeatureSelectionLayer[], AxiosError<APIError[]>>("generic-locations", queryFn);
}
